<template>
    <ion-page>
        <ion-loading :is-open="loading" message="Loading, please wait"/>
        <ion-content>
            <standard-header title="Return" align="middle" />
            <ion-list>
                <ion-item lines="none">
                    <ion-label>
                        <h2>{{ params.upliftItemName }}</h2>
                        <p v-if="scannedItem && scannedItem.skus">{{ scannedItem.skus[0].sku }} | {{scannedItem.item_number}}</p>
                        <p v-else-if="params.upliftItemSku">{{ params.upliftItemSku }} (scan/search to confirm)</p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openItemScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button expand="block" color="primary" size="medium" @click="openItemPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <ion-item lines="none">
                    <ion-label class="ion-align-items-center">
                        <h2>
                            Location
                        </h2>
                        <p>
                            <span v-if="location && location.id">{{ location.code }}</span>
                            <span v-else>Scan or search for a location</span>
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openLocationScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button v-if="web" expand="block" color="primary" size="medium" @click="openLocationPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>

                <ion-item lines="none">
                    <ion-label>Reason</ion-label>
                    <stock-return-reason-select v-model="reason" />
                </ion-item>

                <div v-if="skipUplifts">
                    <ion-item class="ion-padding-top" lines="none">
                        <big-stepper label="Qty Received" v-model="customer_return_qty_received" />
                    </ion-item>
                    <ion-item class="ion-padding-top" lines="none">
                        <big-stepper :max="customer_return_qty_received" label="Qty Accepted" v-model.number="qty_accepted" />
                    </ion-item>
                    <ion-item class="ion-padding-top" lines="none">
                        <big-stepper :max="customer_return_qty_received" label="Qty Binned" v-model.number="qty_binned" />
                    </ion-item>
                </div>
                <div v-else>
                    <ion-item class="ion-padding-top" lines="none">
                        <p>Qty Received</p>
                        <p slot="end">{{params.qty_received}}</p>
                    </ion-item>
                    <ion-item class="ion-padding-top" lines="none">
                        <big-stepper :max="parseInt(params.qty_received)" label="Qty Accepted" v-model.number="qty_accepted" />
                    </ion-item>
                    <ion-item class="ion-padding-top" lines="none">
                        <big-stepper :max="parseInt(params.qty_received)" label="Qty Binned" v-model.number="qty_binned" />
                    </ion-item>
                </div>
            </ion-list>
        </ion-content>
        <ion-footer>

            <ion-button :disabled="!readyToGo" color="warning" class="ion-margin-top" size="large" expand="full" @click="next">
                Next
            </ion-button>
        </ion-footer>
    </ion-page>
</template>

<script>
import {
    alertController,
    IonButton,
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel, IonList,
    IonLoading,
    IonPage
} from "@ionic/vue";
import StandardHeader from "../../../../components/StandardHeader";
import BigStepper from "../../../../components/BigStepper";
import {Capacitor} from "@capacitor/core";
import {barcode, checkmarkCircle, listOutline, search} from "ionicons/icons";
import ItemSearchModalHelper from "../../../../services/ItemSearchModalHelper";
import LocationSearchModalHelper from "../../../../services/LocationSearchModalHelper";
import StockReturnReasonSelect from "../../../../components/StockReturnReasonSelect";

export default {
    name: "UpdateSingle",
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    components: {
        IonLoading,
        IonPage,
        IonContent,
        IonFooter,
        StandardHeader,
        IonItem,
        BigStepper,
        IonIcon,
        IonButton,
        StockReturnReasonSelect,
        IonLabel,
        IonList
    },
    computed: {
        isAmazonUplift() {
            return this.params.id > 0;
        },
        skipUplifts() {
            return this.$route.query.skip_uplifts;
        },
        max() {
            if (this.skipUplifts) {
                return 999999999999999;
            }
            return this.parseInt(this.params.qty_received);
        },
        readyToGo() {
            // we need to parseInt because
            const binned = parseInt(this.qty_binned);
            const accepted = parseInt(this.qty_accepted);
            if (this.skipUplifts) {
                return (binned + accepted == this.customer_return_qty_received) && this.hasScannedItem && this.location.id && this.reason;
            }
            return (accepted + binned == this.params.qty_received) && this.hasScannedItem && this.location.id && this.reason;
        },
        web() {
            return Capacitor.platform === 'web';
        },
        params() {
            return this.$route.params;
        },
        hasScannedItem() {
            return this.scannedItem && this.scannedItem.id > 0;
        },
        include() {
            return 'skus';
        }
    },
    ionViewDidEnter() {
        if (this.isAmazonUplift) {
            this.reason = 'Amazon Overstock';
        }
    },
    watch:{
        params(val) {
            this.refresh();
        }
    },
    mounted() {
        const val = this.params
        if (val.scannedItemId) {
            this.$http.get(`/api/items/${val.scannedItemId}`, {params: {include: this.include}})
                .then(response => {
                    this.scannedItem = response.data;
                });
        }
    },
    methods:{
        next(){
            this.$router.push({
                name: 'warehouse.uplifts.complete',
                params: {
                    id: this.params.id,
                    item: this.scannedItem.id,
                    qty_received: this.skipUplifts?this.customer_return_qty_received:this.params.qty_received,
                    qty_accepted: this.qty_accepted,
                    qty_binned: this.qty_binned,
                    location: this.location.id,
                    reason: this.reason,
                    description: this.params.upliftItemName,
                    item_number: this.scannedItem.item_number,
                }, query: {
                    skip_uplifts: this.skipUplifts
                }
            })
        },
        refresh() {

            this.qty_accepted = 0;
            this.qty_binned = 0;
            this.customer_return_qty_received = 0;

            this.scannedItem = {};
            this.location = {};
            this.reason = null;
            if (this.isAmazonUplift) {
                this.reason = 'Amazon Overstock';
            }
        },
        async openItemScanner() {
            if (Capacitor.platform === 'web') {
                const alert = await alertController.create({
                    header: 'Enter Item #ID',
                    inputs: [{
                        type: 'number',
                        name: 'item_id'
                    }],
                    buttons: ['Ok']
                });
                alert.onDidDismiss().then(input => {
                    const id = input.data.values.item_id;
                    this.$http.get(`/api/items/${id}`, {params: {include: this.include}})
                        .then(response => {
                            this.scannedItem = response.data;
                        });
                });
                alert.present();
                return;
            }
            this.$scanner.search().then(result => {
                this.scannedItem = result;
            }).catch(error => {
                this.scannedItem = {};
                this.$toast.danger(error);
            });
        },
        openItemPicker() {
            ItemSearchModalHelper.open(this.include).then(item => {
                if (! (item.data && item.data.id)) {
                    return;
                }
                this.scannedItem = item.data;
            });
        },
        openLocationScanner() {
            this.$scanner.scan('location').then(result => {
                    this.location = result.extra;
            });
        },
        openLocationPicker() {
            LocationSearchModalHelper.open().then(location => {
                if (! (location.data && location.data.id)) {
                    return;
                }
                this.location = location.data;
            });
        },
    },
    data(){
        return {
            loading: false,

            // customer
            customer_return_qty_received: 0,

            // amazon uplifts
            qty_accepted: 0,
            qty_binned: 0,
            scannedItem: {},
            location: {},
            reason: null
        }
    },
    setup() {
        return {
            checkmarkCircle, search, barcode, listOutline
        }
    }
}
</script>

<style scoped>

</style>